import React, { useState, useRef, useEffect } from "react";
import { sortedArray } from "../network/helper";

function SearchableSelect({
  label,
  value,
  options = [],
  optionLabel = (o) => o.label || "",
  optionValue = (o) => o.value || "",
  placeholder = "Select an option",
  onChange,
  disabled,
  beforeOpen,
  className = "",
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [isTyping, setIsTyping] = useState(false);
  const ref = useRef(null);

  // Close dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (ref.current && !ref.current.contains(e.target)) {
        setIsOpen(false);
        setSearch("");
        setIsTyping(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () =>
      document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const selectedOption = options.find(
    (opt) => optionValue(opt) === value
  );

  const filtered = options.filter((opt) =>
    optionLabel(opt).toLowerCase().startsWith(search.toLowerCase())
  );

  const inputValue = isTyping
    ? search
    : selectedOption
    ? optionLabel(selectedOption)
    : "";

  const showClear =
    !disabled && (isTyping || selectedOption);

  return (
    <div ref={ref} className={`relative ${className}`}>
      {label && (
        <label className="block mb-1 text-[14px] font-medium text-[#374151]">
          {label}
        </label>
      )}

      {/* Input */}
      <div className="relative">
        <input
          type="text"
          disabled={disabled}
          value={inputValue}
          placeholder={placeholder}
          onFocus={async () => {
            if (disabled) return;
            if (beforeOpen) {
              const canOpen = await beforeOpen();
              if (!canOpen) return;
            }
            setIsOpen(true);
            setIsTyping(false);
          }}
          onChange={(e) => {
            setSearch(e.target.value);
            setIsTyping(true);
          }}
          className={`w-full px-3 py-2 pr-14 border border-gray-300 rounded-md !text-[16px]
            ${
              disabled
                ? "!bg-gray-200 cursor-not-allowed"
                : "bg-white focus:border-gray-400"
            }
          `}
        />

        {/* ❌ Clear Icon */}
        {showClear && (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              setSearch("");
              setIsTyping(false);
              onChange("");
            }}
            className="absolute right-7 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-4 w-4"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={2}
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        )}

        {/* Dropdown Arrow */}
        <svg
          className={`absolute right-2 top-1/2 -translate-y-1/2 w-4 h-4 pointer-events-none transition-transform duration-200 ${
            isOpen ? "rotate-180" : ""
          }`}
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M19 9l-7 7-7-7"
          />
        </svg>
      </div>

      {/* Dropdown */}
      {isOpen && !disabled && (
        <div className="absolute z-50 w-full bg-white border border-gray-300 rounded-md mt-1 shadow-md max-h-60 overflow-auto">
          {filtered.length > 0 ? (
            sortedArray(filtered, optionLabel).map((opt) => (
              <div
                key={optionValue(opt)}
                onClick={() => {
                  onChange(optionValue(opt));
                  setIsOpen(false);
                  setSearch("");
                  setIsTyping(false);
                }}
                className={`px-3 py-2 !text-[14px] cursor-pointer hover:bg-gray-100 ${
                  optionValue(opt) === value
                    ? "bg-gray-300 font-semibold"
                    : ""
                }`}
              >
                {optionLabel(opt)}
              </div>
            ))
          ) : (
            <div className="px-3 py-2 text-sm text-gray-400">
              No matches found
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default SearchableSelect;
